import { apiRoutes } from '@/app/apiConfig/apiRoutes';
import { TicketTeamTypes } from '@/app/shared-components/Types/Ticket';
import {
  CreateEditUserPayload,
  CreateUserResponseResult,
  GetUserLogoResponseResult,
  GetUserResponseResult,
  User,
} from '@/app/shared-components/Types/Users';
import { Paged } from '../shared-components/Types/Paged';
import { apiSlice } from './apiSlice';
import { transformPayloadToFormData } from '@/utils/formatters';
import { createMidnightDates, formatStringDateToYYYYmmDD } from '@/utils/dateFormatters';
import {
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
} from 'material-react-table';
import { createMyUtiliesURL, determineCompanyUrl } from '@/utils/helpers';
import { Activity } from 'app/shared-components/Types/Activity';

const imageFields = ['avatar', 'w9'];

export const usersApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getUsers: builder.query<
      Paged<User>,
      {
        filters: MRT_ColumnFiltersState;
        sorting: MRT_SortingState;
        pagination: MRT_PaginationState;
      }
    >({
      query: ({ filters, sorting, pagination }) => {
        const urlParams = createMyUtiliesURL(filters, sorting);

        return {
          url: apiRoutes.users,
          method: 'GET',
          params: {
            page: pagination.pageIndex + 1,
            pageSize: pagination.pageSize,
            ...Object.fromEntries(urlParams),
          },
        };
      },
      providesTags: ['Users'],
    }),
    getAllUsers: builder.query<
      Paged<User>,
      {
        filters: MRT_ColumnFiltersState;
      }
    >({
      query: ({ filters }) => {
        const urlParams = createMyUtiliesURL(filters, []);

        return {
          url: `${apiRoutes.users}`,
          method: 'GET',
          params: {
            ...Object.fromEntries(urlParams),
            ddl: true,
          },
        };
      },
      providesTags: ['Users'],
    }),
    getUsersByTeam: builder.query<Paged<User>, TicketTeamTypes>({
      query: (team: TicketTeamTypes) => {
        return {
          url: `${apiRoutes.users}`,
          method: 'GET',
          params: {
            team: team,
          },
        };
      },
    }),
    getUsersByNameParameter: builder.query<
      Paged<User>,
      {
        name: string;
        roleId?: string | string[];
        companyId?: string;
        includeNullCompanyId?: boolean;
      }
    >({
      query: ({ name, roleId, companyId, includeNullCompanyId }) => {
        const filterSign = '@=*';
        const parameter = 'name';
        const baseUrl = new URL(import.meta.env.VITE_API_URL);
        let roleUrl;
        if (Array.isArray(roleId) && roleId.length > 0) {
          roleUrl = `,roleId==${roleId.join('|')}`;
        }
        if (typeof roleId === 'string') {
          roleUrl = `,roleId==${roleId}`;
        }
        const companyUrl = determineCompanyUrl(companyId, includeNullCompanyId);
        if (roleUrl) {
          baseUrl.searchParams.append(
            'filters',
            `${parameter}${filterSign}${name}${roleUrl}${companyUrl}`,
          );
        } else {
          baseUrl.searchParams.append('filters', `${parameter}${filterSign}${name}${companyUrl}`);
        }

        const url = `${apiRoutes.users}${baseUrl.search}`;
        return {
          url,
          method: 'GET',
        };
      },
    }),
    getUser: builder.query<GetUserResponseResult, string | undefined>({
      query: (userId: string | undefined) => {
        if (userId) {
          return { url: `${apiRoutes.users}/${userId}`, method: 'GET' };
        }
        throw new Error('User id is not defined');
      },
      providesTags: (_result, _error, userId) => [{ type: 'User', id: userId }],
    }),
    getUserLogo: builder.query<GetUserLogoResponseResult, string | undefined>({
      query: (userId: string | undefined) => {
        if (userId) {
          return { url: `${apiRoutes.users}/${userId}/logo`, method: 'GET' };
        }
        throw new Error('User id is not defined');
      },
      providesTags: (_result, _error, userId) => [{ type: 'UserLogo', id: userId }],
    }),
    createUser: builder.mutation<CreateUserResponseResult, CreateEditUserPayload>({
      query: ({ ...patch }) => {
        return {
          url: `${apiRoutes.users}`,
          method: 'POST',
          data: transformPayloadToFormData(patch, imageFields),
        };
      },
      transformResponse: (_response: CreateUserResponseResult, meta: string) => {
        return { id: meta };
      },
      invalidatesTags: (_result, error) =>
        error ? [] : ['Users', 'User', { type: 'UserLogo', id: _result?.id }],
    }),
    // TODO: check what comes as response
    updateUser: builder.mutation<
      GetUserResponseResult,
      { user: CreateEditUserPayload; userId: string }
    >({
      query: ({ user, userId }) => {
        return {
          url: `${apiRoutes.users}/${userId}`,
          method: 'PUT',
          data: transformPayloadToFormData(user, imageFields),
        };
      },
      invalidatesTags: (_result, error, { userId }) =>
        error ? [] : [{ type: 'User', id: userId }, 'Users', { type: 'UserLogo', id: userId }],
    }),
    deleteUser: builder.mutation<void, string>({
      query: (userId) => {
        return {
          url: `${apiRoutes.users}/${userId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (_result, error) => (error ? [] : ['Users']),
    }),
    getScheduledActivitiesForUser: builder.query<Paged<Activity>, { userId?: string; date: Date }>({
      query: ({ userId, date }) => {
        const { midnightOfCurrentUtc, midnightNextDayUtc } = createMidnightDates(date);
        if (userId) {
          return {
            url: `${
              apiRoutes.activities
            }/${userId}?filters=DateScheduled>${midnightOfCurrentUtc.toJSON()},DateScheduled<${midnightNextDayUtc.toJSON()}`,
            method: 'GET',
          };
        }
        throw new Error('userId id is not defined');
      },
      providesTags: ['Activity'],
    }),
    getTotalScheduledActivitiesForUser: builder.query<
      { pastDue: number; activitiesByDate: { dateOnly: string; count: string }[] },
      { activityTypes: string[]; type: 'weekly' | 'daily'; userId?: string; date?: Date | null }
    >({
      query: ({ activityTypes, type, userId, date }) => {
        const formattedDate = formatStringDateToYYYYmmDD(date);
        const params = new URLSearchParams();
        activityTypes.forEach((type) => params.append('actId', type));
        params.append('type', type);
        params.append('date', formattedDate);
        if (userId) {
          return {
            url: `${apiRoutes.activities}/${userId}/total`,
            method: 'GET',
            params,
          };
        }
        throw new Error('userId id is not defined');
      },
    }),
    exportUsers: builder.mutation<
      Blob,
      {
        filters: MRT_ColumnFiltersState;
        sorting: MRT_SortingState;
      }
    >({
      query: ({ filters, sorting }) => {
        const filterData = [...filters];

        const urlParams = createMyUtiliesURL(filterData, sorting);

        return {
          url: `${apiRoutes.users}/filterexport`,
          method: 'GET',
          responseType: 'blob',
          params: {
            ...Object.fromEntries(urlParams),
          },
        };
      },
    }),

    getUsersWhoseCompaniesHaveImportMappings: builder.query<Paged<User>, void>({
      query: () => {
        return {
          url: `${apiRoutes.users}/importmappings`,
          method: 'GET',
          params: {
            pageSize: 10000,
          },
        };
      },
    }),
  }),

  overrideExisting: false,
});

export const {
  useGetUsersQuery,
  useGetAllUsersQuery,
  useGetUsersByTeamQuery,
  useLazyGetUsersByNameParameterQuery,
  useGetUserQuery,
  useLazyGetUserQuery,
  useGetUserLogoQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useLazyGetScheduledActivitiesForUserQuery,
  useLazyGetTotalScheduledActivitiesForUserQuery,
  useGetTotalScheduledActivitiesForUserQuery,
  useExportUsersMutation,
  useGetUsersWhoseCompaniesHaveImportMappingsQuery,
} = usersApiSlice;
